import MarginToolCard from '@AMIEWEB/MarginTool/Common/MarginToolCard';
import { CircularProgress, Divider, Grid, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { MarginToolCardTitle } from '../../../Common/MarginToolCardTitle';
import { MarginToolCurrencyField } from '@AMIEWEB/MarginTool/Common/MarginToolCurrencyField';
import MarginToolTextField from '@AMIEWEB/MarginTool/Common/MarginToolTextField';
import { ControlledTypeAhead } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { useTreeLookupByCategoryName } from '../helper';
import { TreeViewLookupTypes } from '../enum';
import {
  formatTypeAheadOptions,
  getAdditionalPremiumPay,
  getHelperAmount,
  getPayChargeHelperText,
  getPayRateHelperText,
  helperTextWithInfoIcon,
} from './helper';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedPlacementMarginScenario } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { PlacementMarginPayPackageStatus } from 'store/redux-store/margin-tool/slices/placement/placement-pay-package-status/placement-pay-package-status.model';
import { defaultValue, getValueAsCustomDecimal, getValueAsFloat } from '../BillRates/helper';
import { PayRateFieldNames } from './enum';
import { getStatusUpdateButtonDisabledTooltip } from '../../PayPackage/PayPackageHelper';
import { CustomTooltip } from '@AMIEWEB/Common';
import { useIsFieldDisabledInPendingStage } from '../../PayPackage/AddScenario/helper';
import { usePeopleSoftCalculation } from '../../PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { useDebouncedTriggerCalculation, useInitialMarginDetails } from '@AMIEWEB/MarginTool/hooks';
import { selectInitialPlacementMarginScenarioMarginDetailsResponse } from 'store/redux-store/margin-tool/slices/placement/placement-assignment/placement-assignment.selector';
import { findInitialSplitValueBySplitId } from '@AMIEWEB/MarginTool/helper';
import { IPlacementMarginDetailsResponseScenarioSplitItem } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.model';
import { add, subtract } from 'lodash';
import { placementMarginScenarioActions } from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { usePromiseTracker } from 'react-promise-tracker';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';

export const PayRates = ({
  splitIndex,
  field,
}: {
  splitIndex: number;
  field: IPlacementMarginDetailsResponseScenarioSplitItem;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { control, register, setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const scenario = useSelector(selectSelectedPlacementMarginScenario);
  const initialScenarioResponse = useSelector(selectInitialPlacementMarginScenarioMarginDetailsResponse);
  const { triggerPeopleSoftCalculation } = usePeopleSoftCalculation();
  const { initiateRefreshCall } = useInitialMarginDetails();
  const isScenarioStatusPending = scenario?.scenarioStatusId === PlacementMarginPayPackageStatus.Pending;
  const splitData = scenario?.splits[splitIndex];
  const payRateOptions = useTreeLookupByCategoryName(TreeViewLookupTypes.PayRates);
  const [isRefreshIsInProgress, setRefreshIsInProgress] = useState<boolean>(false);
  const isContainerRefreshed = useRef(false);
  const { debouncedTriggerPeopleSoftCalculation } = useDebouncedTriggerCalculation();
  const additionalPremiumPayOptions = useMemo(() => {
    if (!Array.isArray(payRateOptions) && payRateOptions?.fields.length > 0) {
      const options = formatTypeAheadOptions(payRateOptions.fields[0]?.items);

      const updatedOptions = options.map(option =>
        option.object?.description === 'None'
          ? {
              ...option,
              object: {
                ...option.object,
                id: 0,
              },
            }
          : option,
      );

      return updatedOptions;
    }

    return [];
  }, [payRateOptions]);

  const isFieldDisabledInPendingStageForUserRoles = useIsFieldDisabledInPendingStage();
  const payRateValue = watch(`assignmentSplits.${splitIndex}.payRate`);
  const overtimeFactorValue = watch(`assignmentSplits.${splitIndex}.payOvertimeFactor`);
  const callBackFactorValue = watch(`assignmentSplits.${splitIndex}.payCallBackFactor`);
  const holidayFactorValue = watch(`assignmentSplits.${splitIndex}.payHolidayFactor`);
  const doubleTimeFactorValue = watch(`assignmentSplits.${splitIndex}.payDoubletimeFactor`);
  let additionalPremiumPayValue = watch(`assignmentSplits.${splitIndex}.additionalPremiumPay`);

  if (additionalPremiumPayValue === undefined || additionalPremiumPayValue === null) {
    additionalPremiumPayValue = {
      label: 'None',
      object: {
        id: 0,
        description: 'None',
      },
    };
  }

  const additionalPremiumPay =
    !additionalPremiumPayValue || additionalPremiumPayValue?.object?.description === 'None'
      ? defaultValue
      : additionalPremiumPayValue?.object?.description?.toString();

  const { promiseInProgress: isGSACalculateFetchDataInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.gsaCalculateFetchData,
    delay: 0,
  });

  const { promiseInProgress: isPeopleSoftCallMarginToolInProgress } = usePromiseTracker({
    area: PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress,
    delay: 0,
  });

  const handleAdditionalPremiumPayChange = value => {
    setValue(`assignmentSplits.${splitIndex}.additionalPremiumPay`, value);
    triggerPeopleSoftCalculation();
  };

  const computePayCharge = (
    payRollRates: IPlacementMarginDetailsResponseScenarioSplitItem,
    payRateVal: number | string,
  ): string | number => {
    if ((payRollRates?.payrollChargeAmount ?? 0) > 0) {
      return getValueAsFloat(
        add(
          subtract(payRollRates?.payrollChargeAmount ?? 0, payRollRates?.payrollRegularPayRate ?? 0),
          parseFloat(String(payRateVal)),
        ),
      );
    }
    return 0;
  };

  const handlePayChargeUpdate = e => {
    const payRollRates = isContainerRefreshed.current
      ? findInitialSplitValueBySplitId(initialScenarioResponse, field?.splitId)
      : field;
    setValue(`assignmentSplits.${splitIndex}.payCharge`, computePayCharge(payRollRates, e?.target?.value));
  };

  useEffect(() => {
    setValue(`assignmentSplits.${splitIndex}.payCharge`, computePayCharge(field, field?.payRate ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldUpdatesAfterRefresh = useCallback(
    (initialSplit: IPlacementMarginDetailsResponseScenarioSplitItem) => {
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(true));
      setValue(`assignmentSplits.${splitIndex}.payRate`, getValueAsFloat(initialSplit?.payRate), {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.payOvertimeFactor`, getValueAsFloat(initialSplit?.payOvertimeFactor), {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.payCallBackFactor`, getValueAsFloat(initialSplit?.payCallBackFactor), {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.payHolidayFactor`, getValueAsFloat(initialSplit?.payHolidayFactor), {
        shouldDirty: true,
      });
      setValue(
        `assignmentSplits.${splitIndex}.payDoubletimeFactor`,
        getValueAsFloat(initialSplit?.payDoubletimeFactor),
        {
          shouldDirty: true,
        },
      );
      setValue(`assignmentSplits.${splitIndex}.payOnCallRate`, getValueAsFloat(initialSplit?.payOnCallRate), {
        shouldDirty: true,
      });
      setValue(
        `assignmentSplits.${splitIndex}.payCharge`,
        computePayCharge(initialSplit, initialSplit?.payRate ?? 0),
        { shouldDirty: true },
      );
      setValue(
        `assignmentSplits.${splitIndex}.payAmountPerMile`,
        getValueAsCustomDecimal(initialSplit?.payAmountPerMile, 3),
        {
          shouldDirty: true,
        },
      );
      setValue(`assignmentSplits.${splitIndex}.payGuaranteedHours`, getValueAsFloat(initialSplit?.payGuaranteedHours), {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.payPreceptor`, getValueAsFloat(initialSplit?.payPreceptor), {
        shouldDirty: true,
      });
      setValue(`assignmentSplits.${splitIndex}.additionalPremiumPay`, additionalPremiumPayOptions[0], {
        shouldDirty: true,
      });
      dispatch(placementMarginScenarioActions.setPlacementMarginGroupUpdateInprogressStatus(false));
      debouncedTriggerPeopleSoftCalculation();
      setRefreshIsInProgress(false);
      isContainerRefreshed.current = true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue, debouncedTriggerPeopleSoftCalculation, setRefreshIsInProgress, splitIndex],
  );

  useEffect(() => {
    if (isRefreshIsInProgress && initialScenarioResponse) {
      const initialSplit = findInitialSplitValueBySplitId(initialScenarioResponse, field?.splitId);
      if (initialSplit) handleFieldUpdatesAfterRefresh(initialSplit);
      //setRefreshIsInProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScenarioResponse, isRefreshIsInProgress, splitIndex]);

  return (
    <MarginToolCard
      id="benefits-card-marginToolDetailsPage"
      title={<MarginToolCardTitle title={t('marginTool.components.assignment.payRate.title')} />}
      showRefreshButton={!isRefreshIsInProgress}
      icon={<CircularProgress size={16} color="inherit" />}
      isCustomIcon={isRefreshIsInProgress}
      onRefresh={() => {
        initiateRefreshCall();
        setRefreshIsInProgress(true);
      }}
      customBackGroundColor={theme.palette.framework.system.white}
      children={
        <>
          <Grid item container spacing={2} sx={{ p: 2, pb: 0 }}>
            <Grid item xs={3}>
              <MarginToolCurrencyField
                id="marginTool-payRate-currencyField"
                name={`assignmentSplits.${splitIndex}.payRate`}
                label={t('marginTool.components.assignment.payRate.title')}
                tooltip={{ disabled: true }}
                defaultValue={getValueAsFloat(splitData?.payRate)}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                helperText={getPayRateHelperText(splitData, splitIndex, t)}
                triggerMarginToolFormUpdates={() => triggerPeopleSoftCalculation()}
                onChange={e => handlePayChargeUpdate(e)}
              />
            </Grid>
          </Grid>

          <Grid item sx={{ p: 2 }}>
            <Divider />
          </Grid>

          <Grid item container spacing={2} sx={{ pl: 2, pr: 2 }}>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-overtimeFactor-textField"
                name={`assignmentSplits.${splitIndex}.payOvertimeFactor`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.overtimeFactor')}
                startAdornmentValue={t('marginTool.labels.crossSymbol')}
                maxNumbersAllowed={3}
                isDecimalNumber={true}
                defaultValue={getValueAsFloat(splitData?.payOvertimeFactor)}
                helperText={getHelperAmount(PayRateFieldNames.overtimeFactor, overtimeFactorValue, payRateValue, t)}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-callBackFactor-textField"
                name={`assignmentSplits.${splitIndex}.payCallBackFactor`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.callBackFactor')}
                startAdornmentValue={t('marginTool.labels.crossSymbol')}
                maxNumbersAllowed={3}
                isDecimalNumber={true}
                defaultValue={getValueAsFloat(splitData?.payCallBackFactor)}
                helperText={helperTextWithInfoIcon(
                  PayRateFieldNames.callBackFactor,
                  callBackFactorValue,
                  payRateValue,
                  t,
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-holidayFactor-textField"
                name={`assignmentSplits.${splitIndex}.payHolidayFactor`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.holidayFactor')}
                startAdornmentValue={t('marginTool.labels.crossSymbol')}
                maxNumbersAllowed={3}
                isDecimalNumber={true}
                defaultValue={getValueAsFloat(splitData?.payHolidayFactor)}
                helperText={helperTextWithInfoIcon(
                  PayRateFieldNames.holidayFactor,
                  holidayFactorValue,
                  payRateValue,
                  t,
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-doubleTimeFactor-textField"
                name={`assignmentSplits.${splitIndex}.payDoubletimeFactor`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.doubleTimeFactor')}
                startAdornmentValue={t('marginTool.labels.crossSymbol')}
                maxNumbersAllowed={3}
                isDecimalNumber={true}
                defaultValue={getValueAsFloat(splitData?.payDoubletimeFactor)}
                helperText={getHelperAmount(PayRateFieldNames.doubleTimeFactor, doubleTimeFactorValue, payRateValue, t)}
              />
            </Grid>
          </Grid>

          <Grid item sx={{ p: 2 }}>
            <Divider />
          </Grid>

          <Grid item container spacing={2} sx={{ pl: 2, pr: 2 }}>
            <Grid item xs={3}>
              <MarginToolCurrencyField
                id="marginTool-payRate-onCallRate-currencyField"
                name={`assignmentSplits.${splitIndex}.payOnCallRate`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.onCallRate')}
                tooltip={{ disabled: true }}
                defaultValue={getValueAsFloat(splitData?.payOnCallRate)}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolCurrencyField
                id="marginTool-payRate-charge-currencyField"
                name={`assignmentSplits.${splitIndex}.payCharge`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.charge')}
                tooltip={{ disabled: true }}
                defaultValue={getValueAsFloat(splitData?.payCharge)}
                helperText={getPayChargeHelperText(
                  isContainerRefreshed.current
                    ? findInitialSplitValueBySplitId(initialScenarioResponse, field?.splitId)
                    : field,
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-amountPerMile-currencyField"
                label={t('marginTool.labels.amountPerMile')}
                name={`assignmentSplits.${splitIndex}.payAmountPerMile`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                maxNumbersAllowed={3}
                maxDecimals={3}
                startAdornmentValue={t('currency.dollar')}
                defaultValue={getValueAsCustomDecimal(splitData?.payAmountPerMile, 3)}
                isDecimalNumber={true}
              />
            </Grid>
            <Grid item xs={3}>
              <MarginToolTextField
                id="marginTool-payRate-guaranteedHours-textField"
                name={`assignmentSplits.${splitIndex}.payGuaranteedHours`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.guaranteedHours')}
                endAdornmentValue={t('marginTool.labels.hours')}
                maxNumbersAllowed={2}
                isDecimalNumber={true}
                defaultValue={getValueAsFloat(splitData?.payGuaranteedHours)}
              />
            </Grid>
          </Grid>

          <Grid item container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={3}>
              <MarginToolCurrencyField
                id="marginTool-payRate-preceptor-currencyField"
                name={`assignmentSplits.${splitIndex}.payPreceptor`}
                disabled={
                  !isScenarioStatusPending || isGSACalculateFetchDataInProgress || isPeopleSoftCallMarginToolInProgress
                }
                label={t('marginTool.labels.preceptor')}
                tooltip={{ disabled: true }}
                defaultValue={getValueAsFloat(splitData?.payPreceptor)}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomTooltip
                tooltipContent={isFieldDisabledInPendingStageForUserRoles ? getStatusUpdateButtonDisabledTooltip() : ''}
              >
                <ControlledTypeAhead
                  id="marginTool-payRate-additionalPremiumPay-dropdown"
                  name={`assignmentSplits.${splitIndex}.additionalPremiumPay`}
                  label={t('marginTool.labels.additionalPremiumPay')}
                  register={register}
                  control={control}
                  options={additionalPremiumPayOptions || []}
                  showDropdownIcon={true}
                  mandatory={false}
                  filledNormal={false}
                  disabled={
                    !isScenarioStatusPending ||
                    isFieldDisabledInPendingStageForUserRoles ||
                    isGSACalculateFetchDataInProgress ||
                    isPeopleSoftCallMarginToolInProgress
                  }
                  defaultValue={additionalPremiumPayValue}
                  customScroll={true}
                  onChange={e => handleAdditionalPremiumPayChange(e)}
                  helperText={
                    additionalPremiumPayValue?.object?.description !== 'None'
                      ? getHelperAmount(PayRateFieldNames.additionalPremiumPay, additionalPremiumPay, payRateValue, t)
                      : undefined
                  }
                />
              </CustomTooltip>
            </Grid>
          </Grid>
        </>
      }
    />
  );
};
